import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { showAppAlert } from "../../store/reducers/appreducer";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  CssBaseline,
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import zxcvbn from "zxcvbn"; 
import resetPwdImage from "../../assets/signin.png";
import logoImage from "../../assets/logo.png";
import { baseURL } from "../../config";

const Root = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  overflow: "hidden", 
}));

const LeftSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor: "#FFFFFF",
  maxWidth: "50%",
}));

const RightSide = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#F3F7FF",
}));

const ImageContainer = styled("div")(({ theme }) => ({
  width: "514px",
  height: "467px",
  backgroundImage: `url(${resetPwdImage})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: "45.78px 17.89px 45.78px 26.27px",
}));

const FormContainer = styled(Container)(({ theme }) => ({
  maxWidth: "400px",
  margin: "0 auto",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const Logo = styled("img")(({ theme }) => ({
  width: "51.12px",
  height: "50.64px",
  opacity: "1",
  marginBottom: "12px",
}));

const LogoText = styled(Typography)(({ theme }) => ({
  marginLeft: "5px",
  marginTop: "20px",
  fontFamily: "Raleway",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "39.01px",
  textAlign: "left",
  color: "#1E186B",
  marginBottom: "3px",
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  width: "250px",
  height: "38px",
  top: "212px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "32px",
  fontWeight: 600,
  lineHeight: "37.57px",
  textAlign: "left",
  color: "#5088FF",
  marginBottom: "12px",
}));

const ResetPwdText = styled(Typography)(({ theme }) => ({
  width: "450px",
  height: "33px",
  top: "258px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "32.87px",
  textAlign: "left",
  color: "#313D4F",
  marginBottom: "6px",
}));

const DetailsText = styled(Typography)(({ theme }) => ({
  width: "272px",
  height: "19px",
  top: "302px",
  left: "123px",
  fontFamily: "Raleway",
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "18.78px",
  textAlign: "left",
  color: "#67768D",
  marginBottom: "24px",
}));

const CustomButton = styled(Button)(({ theme }) => ({
  width: "100%",
  height: "56px",
  padding: "8px 8px 8px 8px",
  gap: "8px",
  borderRadius: "8px 8px 8px 8px",
  backgroundColor: "#5088FF",
  color: "#FFFFFF",
  marginTop: theme.spacing(8),
}));

const PasswordStrength = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginTop: theme.spacing(1),
  width: "100%",
}));

const PasswordStrengthBar = styled(LinearProgress)(({ theme, strength }) => ({
  height: "8px",
  borderRadius: "4px",
  backgroundColor: "#e0e0e0",
  flex: 1,
  "& .MuiLinearProgress-bar": {
    backgroundColor:
      strength === 4
        ? "#4caf50"
        : strength === 3
        ? "#8bc34a"
        : strength === 2
        ? "#ffeb3b"
        : strength === 1
        ? "#ff9800"
        : "#f44336",
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Raleway",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22.4px",
  textAlign: "left",
  color: "#262626",
  marginBottom: theme.spacing(0.3),
}));



const ResetPwd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch() ;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [data, setData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [token, setToken] = useState("");
  const [success, setSuccess] = useState(false);


  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get("token");
    if (resetToken) {
      setToken(resetToken);
    } else {
      dispatch(
        showAppAlert({
          show:true,
          alertType:"error", 
          alertMessage:"INVALID TOKEN OR TOKEN NOT AVAILABLE", 
        })
      )
    }
  }, [location, navigate]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "password") {
      const strength = zxcvbn(value);
      setPasswordStrength(strength.score);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();

    if (isValid) {
      setLoading(true);
      try {
        const response = await axios.post(`${baseURL}resetPassword/`, {
          reset_token: token,
          new_password: data.password,
          confirm_new_password: data.confirmPassword,
        });

        dispatch(
          showAppAlert({
            show:true,
            alertType:"success", 
            alertMessage:"Password has been successfully reset! GO BACK TO SIGN IN PAGE .", 
          })
        )
      } catch (error) {
        setLoading(false);
        const message = error.response?.data?.message || "Something went wrong.";
        // alert(message);
        dispatch(
          showAppAlert({
            show:true,
            alertType:"error", 
            alertMessage:message, 
          })
        )
      } finally {
        setLoading(false);
        setSuccess(true); 
      }
    }
  };


  const validateForm = () => {
    let tempErrors = {};
    tempErrors.password = !data.password;
    tempErrors.confirmPassword = data.password !== data.confirmPassword;

    setErrors(tempErrors);
    return Object.values(tempErrors).every((error) => !error);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleRedirect = () => {
    navigate("https://app.hire-best.ai/signin") ;
  };


  return (
    success ? (
      <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <Typography variant="h5">
        Your password has been reset successfully!
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleRedirect}
        style={{ marginTop: "20px" }}
      >
        Go to SIGN IN
      </Button>
    </Box>
    ):
    (
    <Root>
      <CssBaseline />
      <LeftSide>
        <FormContainer>
          <LogoContainer>
            <Logo src={logoImage} alt="Logo" />
            <LogoText>HR-AI</LogoText>
          </LogoContainer>
          <WelcomeText>Welcome</WelcomeText>
          <ResetPwdText>Enter your new Password to reset</ResetPwdText>
          <DetailsText>Enter your details to proceed further</DetailsText>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FieldLabel>New Password*</FieldLabel>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  name="password"
                  placeholder="Enter New Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={data.password}
                  onChange={handleChange}
                  error={errors.password}
                  helperText={errors.password && "Password is required"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Confirm Password*</FieldLabel>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  name="confirmPassword"
                  placeholder="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={data.confirmPassword}
                  onChange={handleChange}
                  error={errors.confirmPassword}
                  helperText={
                    errors.confirmPassword &&
                    "Passwords must match"
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <PasswordStrength>
              <PasswordStrengthBar
                variant="determinate"
                value={100}
                strength={passwordStrength}
              />
              <Typography variant="body2" style={{ marginLeft: "8px" }}>
                {passwordStrength === 4
                  ? "Strong"
                  : passwordStrength === 3
                  ? "Good"
                  : passwordStrength === 2
                  ? "Fair"
                  : passwordStrength === 1
                  ? "Weak"
                  : "Very Weak"}
              </Typography>
            </PasswordStrength>
            <CustomButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </CustomButton>
          </form>
        </FormContainer>
      </LeftSide>
      <RightSide>
        <ImageContainer />
      </RightSide>
    </Root>
    )
  );
};

export default ResetPwd;
